import { useContext, useEffect, useRef, useState } from "react";
import { Handle, Position } from "@xyflow/react";
import { FaYoutube } from "react-icons/fa";
import { IoPlayOutline } from "react-icons/io5";
import { BiLoaderCircle } from "react-icons/bi";
import axios from "./../../utils/axiosInstance";
import { useDispatch, useSelector } from "react-redux";
import { ungroupNode, updateNode } from "../../utils/flowSlice";
import { FaRegObjectUngroup } from "react-icons/fa6";
import { DarkModeContext } from "../../context/DarkModeContext";
import toast from "react-hot-toast";

const YoutubeNode = ({ data, isConnectable }) => {
  const [playButtonClicked, setPlayButtonClicked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState(data.title || "Fetching the title");
  const script = data.script || "";
  const [note, setNote] = useState(data.note || "");
  const dispatch = useDispatch();
  const sourceUrl = data.sourceUrl;

  const nodes = useSelector((store) => store.flow.nodes);
  const currentNode = nodes.find((node) => node.id === data.id);
  const isGrouped = !!currentNode ? !!currentNode.parentId : false;
  const unGroup = () =>
    dispatch(
      ungroupNode({
        id: data.id,
      })
    );

  const extractVideoId = (url) => {
    const match = url.match(
      /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)/
    );
    return match ? match[1] : null;
  };

  const fetchScriptAndTitle = async (url) => {
    try {
      if (title !== "Fetching the title" && script !== "") return;
      setLoading(true);

      const response1 = await axios.post(
        `${process.env.REACT_APP_BASED_URL}/service/youtube/script`,
        {
          url,
        }
      );
      const tempScript = response1.data.script;
      const tempTitle = response1.data.title;

      setLoading(false);
      setTitle(tempTitle);
      dispatch(
        updateNode({
          id: data.id,
          data: {
            ...data,
            script: tempScript,
            title: tempTitle,
          },
        })
      );
    } catch (err) {
      console.log(err);
      if (err.status === 402) {
        toast.error(
          "You have run out of your credit. You need to upgrade your plan"
        );
      }
    }
  };

  useEffect(() => {
    fetchScriptAndTitle(sourceUrl);
  }, [sourceUrl]);

  const { darkMode } = useContext(DarkModeContext);
  const textAreaRef = useRef(null);
  const handleInputChange = (e) => {
    setNote(e.target.value);
    if (e.target.scrollHeight > 50) {
      e.target.style.height = `${e.target.scrollHeight}px`;
    }
    if (e.target.value === "") {
      e.target.style.height = `${50}px`;
    }
    dispatch(
      updateNode({
        id: data.id,
        data: {
          ...data,
          note: e.target.value,
        },
      })
    );
  };

  return (
    <div>
      <Handle
        type="source"
        position={Position.Right}
        isConnectable={isConnectable}
        style={{
          width: "20px",
          height: "20px",
          background: "white",
          border: "2px solid #32b5e5",
          borderRadius: "50%",
          position: "absolute",
          right: "-20px",
          transform: "translateY(-50%)",
          display: "flex",
          alignItems: "center",
          lineHeight: "20px",
          fontSize: "12px",
          justifyContent: "center",
          transition: "transform 0.2s, background-color 0.2s, right 0.2s",
          willChange: "transform",
        }}
        onMouseEnter={(e) => {
          e.target.style.transform = "translateY(-50%) scale(1.5)";
          e.target.style.background = "#32b5e5";
          e.target.style.color = "white";
          e.target.innerHTML = "+";
        }}
        onMouseLeave={(e) => {
          e.target.style.transform = "translateY(-50%) scale(1)";
          e.target.style.background = "white";
          e.target.style.color = "";
          e.target.innerHTML = "";
        }}
      />
      <div
        className={`flex flex-col gap-1 w-[375px] mx-auto rounded-[10px] ${
          darkMode ? "bg-[#eeeee6]" : "bg-[#080808]"
        }`}
        tabIndex="0"
      >
        <div className="flex justify-between items-center bg-red-600 text-white px-4 py-2 rounded-[9px]">
          <div className="flex items-center space-x-2">
            {isGrouped && (
              <FaRegObjectUngroup
                size={"16"}
                className="hover:cursor-pointer"
                onClick={() => unGroup()}
              />
            )}
            {loading ? (
              <div className="flex items-center justify-start">
                <BiLoaderCircle
                  size={"18"}
                  className="loading-icon"
                  color="white"
                />
                <span className="ml-2 flex justify-start font-semibold text-xs">
                  Fetching the title
                </span>
              </div>
            ) : (
              <div className="flex items-center justify-start ">
                <FaYoutube size={"18"} />
                <span className="ml-2 font-semibold text-xs text-left overflow-hidden text-nowrap overflow-ellipsis w-[250px]">
                  {title}
                </span>
              </div>
            )}
          </div>
        </div>

        {playButtonClicked ? (
          <div className="relative w-full h-full">
            <iframe
              className="w-full h-[260px] rounded-[8px] object-cover"
              src={`https://www.youtube.com/embed/${extractVideoId(
                data.sourceUrl
              )}?autoplay=1`}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title="YouTube video"
            ></iframe>
          </div>
        ) : (
          <div className="relative w-full">
            <img
              src={`https://i.ytimg.com/vi/${extractVideoId(
                data.sourceUrl
              )}/maxresdefault.jpg`}
              alt="youtube thumbnail"
              className="w-full h-[260px] rounded-[8px]"
            />
            <button
              className="absolute flex items-center justify-center bg-black bg-opacity-50 text-white rounded-[12px] text-[48px] transition duration-300 hover:bg-red-600"
              style={{
                width: "100px",
                height: "80px",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              }}
              onClick={() => setPlayButtonClicked(true)}
            >
              <IoPlayOutline />
            </button>
          </div>
        )}

        {/* Notes Section */}
        <textarea
          ref={textAreaRef}
          className={` nodrag w-full h-[36px] min-h-[50px] max-h-[120px] ${
            darkMode
              ? "bg-[#f5f8fa] border-neutral-300 text-black"
              : "bg-[#292929] border-[#292929] text-neutral-200"
          } text-sm font-sans  p-1.5 pr-3 rounded-[8px]  focus-within:outline-none resize-y `}
          placeholder={"Add notes for AI to use..."}
          value={note}
          onChange={(e) => handleInputChange(e)}
          rows={1}
        ></textarea>
      </div>
    </div>
  );
};

export default YoutubeNode;
